import React, { useEffect, useState } from 'react';
import { Users, TrendingUp, Clock, Target, Search, MapPin } from 'lucide-react';
import Sidebar from '../../components/ui/Sidebar';
import Header from '../../components/ui/Header';
import { partnerAcquisitionService, PartnerProspect, PipelineMetrics } from '../../services/partnerAcquisitionService';

const PartnerAcquisitionPipeline: React.FC = () => {
  const [prospects, setProspects] = useState<PartnerProspect[]>([]);
  const [metrics, setMetrics] = useState<PipelineMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedStage, setSelectedStage] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [leadScores, setLeadScores] = useState<Record<string, number>>({});

  useEffect(() => {
    loadData();
  }, [selectedStage]);

  const loadData = async () => {
    try {
      setLoading(true);
      const filters = selectedStage !== 'all' ? { status: selectedStage } : undefined;
      const [prospectsData, metricsData] = await Promise.all([
        partnerAcquisitionService.getPartnerProspects(filters),
        partnerAcquisitionService.getPipelineMetrics()
      ]);
      
      setProspects(prospectsData);
      setMetrics(metricsData);

      // Load lead scores for each prospect
      const scores: Record<string, number> = {};
      for (const prospect of prospectsData) {
        scores[prospect.id] = await partnerAcquisitionService.getLeadScoring(prospect.id);
      }
      setLeadScores(scores);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredProspects = prospects.filter(prospect =>
    prospect.business_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    prospect.contact_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    prospect.contact_email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      pending: 'bg-yellow-100 text-yellow-800',
      pre_approved: 'bg-blue-100 text-blue-800',
      active: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStageLabel = (status: string) => {
    const labels: Record<string, string> = {
      pending: 'Initial Contact',
      pre_approved: 'Qualified Lead',
      active: 'Active Partner',
      rejected: 'Rejected'
    };
    return labels[status] || status;
  };

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <Header />
        <main className="flex-1 overflow-y-auto">
          <div className="max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {/* Header Section */}
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Partner Acquisition Pipeline</h1>
              <p className="text-gray-600">Comprehensive lead management and prospect tracking for Vienna tourism partnerships</p>
            </div>

            {/* KPI Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-3 bg-blue-100 rounded-lg">
                    <Users className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
                <div className="text-2xl font-bold text-gray-900 mb-1">
                  {metrics?.total_prospects || 0}
                </div>
                <div className="text-sm text-gray-600">Total Prospects</div>
              </div>

              <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-3 bg-green-100 rounded-lg">
                    <Target className="w-6 h-6 text-green-600" />
                  </div>
                </div>
                <div className="text-2xl font-bold text-gray-900 mb-1">
                  {metrics?.qualified_leads || 0}
                </div>
                <div className="text-sm text-gray-600">Qualified Leads</div>
              </div>

              <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-3 bg-purple-100 rounded-lg">
                    <TrendingUp className="w-6 h-6 text-purple-600" />
                  </div>
                </div>
                <div className="text-2xl font-bold text-gray-900 mb-1">
                  {metrics?.conversion_rate || 0}%
                </div>
                <div className="text-sm text-gray-600">Conversion Rate</div>
              </div>

              <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-3 bg-orange-100 rounded-lg">
                    <Clock className="w-6 h-6 text-orange-600" />
                  </div>
                </div>
                <div className="text-2xl font-bold text-gray-900 mb-1">
                  {metrics?.avg_deal_closure_time || 0} days
                </div>
                <div className="text-sm text-gray-600">Avg Deal Closure Time</div>
              </div>
            </div>

            {/* Filters and Search */}
            <div className="bg-white rounded-lg shadow-sm p-6 mb-6 border border-gray-200">
              <div className="flex flex-col lg:flex-row gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    placeholder="Search by business name, contact, or email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                
                <div className="flex gap-4">
                  <select
                    value={selectedStage}
                    onChange={(e) => setSelectedStage(e.target.value)}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="all">All Stages</option>
                    <option value="pending">Initial Contact</option>
                    <option value="pre_approved">Qualified Lead</option>
                    <option value="active">Active Partner</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Pipeline Table */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Business
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Contact
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Lead Score
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Stage
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Application Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Next Action
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {loading ? (
                      <tr>
                        <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                          Loading prospects...
                        </td>
                      </tr>
                    ) : filteredProspects.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                          No prospects found
                        </td>
                      </tr>
                    ) : (
                      filteredProspects.map((prospect) => (
                        <tr key={prospect.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4">
                            <div className="flex items-center">
                              <div>
                                <div className="text-sm font-medium text-gray-900">
                                  {prospect.business_name}
                                </div>
                                <div className="text-sm text-gray-500 flex items-center mt-1">
                                  <MapPin className="w-3 h-3 mr-1" />
                                  {prospect.business_address}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-900">{prospect.contact_name || 'N/A'}</div>
                            <div className="text-sm text-gray-500">{prospect.contact_email}</div>
                            {prospect.contact_phone && (
                              <div className="text-sm text-gray-500">{prospect.contact_phone}</div>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center">
                              <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                                <div
                                  className="bg-blue-600 h-2 rounded-full"
                                  style={{ width: `${leadScores[prospect.id] || 0}%` }}
                                />
                              </div>
                              <span className="text-sm font-medium text-gray-900">
                                {leadScores[prospect.id] || 0}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(prospect.status)}`}>
                              {getStageLabel(prospect.status)}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500">
                            {new Date(prospect.application_date).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-900">
                              {prospect.calendar_slot_selected ? 'Schedule onboarding call' : 'Send calendar link'}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default PartnerAcquisitionPipeline;